/**
 * remarkCellCogTags — mdast plugin that recognizes CellCog's own inline
 * tags and converts them into custom nodes:
 *
 *   <SHOW_FILE [attr="val"...]>path/or/blob/name</SHOW_FILE>
 *       → cellcogShowFile node
 *   <GENERATE_FILE [attr="val"...]></GENERATE_FILE>
 *       → cellcogGenerateFile node
 *
 * Coverage:
 *   The plugin walks three kinds of mdast nodes:
 *     1. `text`       — tags anywhere in prose
 *     2. `inlineCode` — tags inside backticks, e.g. inside a table cell
 *     3. `code`       — tags inside fenced code blocks
 *
 *   For `text` nodes, matching tags become sibling AST nodes (with text
 *   fragments preserved on either side).
 *
 *   For `inlineCode` / `code` nodes, the node is split: any non-tag prefix
 *   becomes a shorter `inlineCode` / `code` node (same language for code
 *   blocks), the tag becomes a cellcogShowFile / cellcogGenerateFile node,
 *   and the non-tag suffix becomes another shorter node. If the prefix or
 *   suffix is empty, it is omitted.
 *
 *   This means `<SHOW_FILE>...</SHOW_FILE>` rendered literally into a
 *   fenced code block or inline backticks will still resolve to the
 *   actual file / chip. The "ugly signed URL leaked into a code block"
 *   bug is avoided because we're transforming the tag itself, not
 *   preprocessing the whole content string.
 *
 * Media index:
 *   After transforming the tree, we do a second pass that assigns a
 *   stable `mediaIndex` (0-based document order) to each
 *   cellcogShowFile node. The renderer uses this to build the mediaClick
 *   id that the slideshow/full-viewer listens for. Computing the index
 *   at the AST level (instead of during render) makes it stable across
 *   re-renders — fixing the bug where clicking any image always opened
 *   the first one.
 */
import { SKIP, visit } from 'unist-util-visit';

const SHOW_FILE_RE = /<SHOW_FILE(\s[^>]*)?>([\s\S]*?)<\/SHOW_FILE>/g;
const GENERATE_FILE_RE = /<GENERATE_FILE(\s[^>]*)?>([\s\S]*?)<\/GENERATE_FILE>/g;

/**
 * Returns a document-ordered list of tag matches in the given string.
 * Overlapping matches are resolved by taking the earliest and skipping
 * any that start before the previous ended.
 */
const findAllTagMatches = (text) => {
  const matches = [];

  SHOW_FILE_RE.lastIndex = 0;
  let m;
  while ((m = SHOW_FILE_RE.exec(text)) !== null) {
    matches.push({
      kind: 'showFile',
      start: m.index,
      end: m.index + m[0].length,
      attrStr: m[1] || '',
      inner: m[2] || '',
    });
  }

  GENERATE_FILE_RE.lastIndex = 0;
  while ((m = GENERATE_FILE_RE.exec(text)) !== null) {
    matches.push({
      kind: 'generateFile',
      start: m.index,
      end: m.index + m[0].length,
      attrStr: m[1] || '',
      inner: m[2] || '',
    });
  }

  matches.sort((a, b) => a.start - b.start);

  const resolved = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start < cursor) continue;
    resolved.push(match);
    cursor = match.end;
  }
  return resolved;
};

const snakeToCamel = (s) =>
  s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

const parseAttrs = (attrStr) => {
  const out = {};
  if (!attrStr) return out;
  const re = /([a-zA-Z_][a-zA-Z0-9_-]*)="([^"]*)"/g;
  let m;
  while ((m = re.exec(attrStr)) !== null) {
    out[snakeToCamel(m[1])] = m[2];
  }
  return out;
};

const makeShowFileNode = (fileUri, attrs) => ({
  type: 'cellcogShowFile',
  fileUri,
  attributes: attrs,
  data: {
    hName: 'cellcog-show-file',
    hProperties: { fileUri, ...attrs },
  },
});

const makeGenerateFileNode = (attrs) => ({
  type: 'cellcogGenerateFile',
  attributes: attrs,
  data: {
    hName: 'cellcog-generate-file',
    hProperties: { ...attrs },
  },
});

/**
 * Build the replacement array for a given string value, using a supplied
 * constructor for the "non-tag fragment" nodes (e.g. text / inlineCode / code).
 * Returns null if no tags were found so the caller can skip the splice.
 */
const buildReplacement = (value, makeFragmentNode) => {
  const matches = findAllTagMatches(value);
  if (matches.length === 0) return null;

  const out = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start > cursor) {
      const frag = value.slice(cursor, match.start);
      if (frag.length > 0) out.push(makeFragmentNode(frag));
    }
    const attrs = parseAttrs(match.attrStr);
    if (match.kind === 'showFile') {
      out.push(makeShowFileNode(match.inner.trim(), attrs));
    } else {
      out.push(makeGenerateFileNode(attrs));
    }
    cursor = match.end;
  }
  if (cursor < value.length) {
    const frag = value.slice(cursor);
    if (frag.length > 0) out.push(makeFragmentNode(frag));
  }
  return out;
};

const remarkCellCogTags = () => {
  return (tree) => {
    // Pass 1 — transform text nodes (prose)
    visit(tree, 'text', (node, index, parent) => {
      if (!parent || typeof index !== 'number') return;
      if (typeof node.value !== 'string') return;

      const replacement = buildReplacement(node.value, (frag) => ({
        type: 'text',
        value: frag,
      }));
      if (!replacement) return;

      parent.children.splice(index, 1, ...replacement);
      return [SKIP, index + replacement.length];
    });

    // Pass 2 — transform inlineCode nodes (backticks, e.g. inside a
    // table cell). A prefix/suffix of the original inlineCode stays as
    // inlineCode so styling is preserved for any surrounding text.
    visit(tree, 'inlineCode', (node, index, parent) => {
      if (!parent || typeof index !== 'number') return;
      if (typeof node.value !== 'string') return;

      const replacement = buildReplacement(node.value, (frag) => ({
        type: 'inlineCode',
        value: frag,
      }));
      if (!replacement) return;

      parent.children.splice(index, 1, ...replacement);
      return [SKIP, index + replacement.length];
    });

    // Pass 3 — transform code nodes (fenced code blocks). The original
    // `lang` and `meta` are preserved on every code fragment so Shiki
    // keeps syntax-highlighting the surrounding snippet correctly.
    visit(tree, 'code', (node, index, parent) => {
      if (!parent || typeof index !== 'number') return;
      if (typeof node.value !== 'string') return;

      const { lang, meta } = node;
      const replacement = buildReplacement(node.value, (frag) => ({
        type: 'code',
        lang,
        meta,
        value: frag.replace(/^\n+|\n+$/g, ''), // trim stray newlines at the split boundary
      }));
      if (!replacement) return;

      parent.children.splice(index, 1, ...replacement);
      return [SKIP, index + replacement.length];
    });

    // Pass 4 — assign a stable mediaIndex (document order) to each
    // cellcogShowFile node. This is the id the slideshow picks up on
    // click; computing it here (rather than at render time) keeps it
    // stable across re-renders.
    let mediaIndex = 0;
    visit(tree, 'cellcogShowFile', (node) => {
      if (!node.data) node.data = {};
      if (!node.data.hProperties) node.data.hProperties = {};
      node.data.hProperties.mediaIndex = mediaIndex;
      mediaIndex += 1;
    });
  };
};

export default remarkCellCogTags;
